//This is for creation and deletion of categories in the super admin dashboard.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';

import {
  createCategory,
  getCategories,
  deleteCategory,
  updateCategory,
} from '../../services/categories.services';
import { Trash2, Pencil } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface Category {
  id: string;
  name: string;
  description: string;
  isHighSchool: boolean;
  isElementary: boolean;
}

const CategoriesManagement: React.FC = () => {
  const { authStatus } = useAuth();
  const [categoryName, setCategoryName] = useState('');
  const [isHighSchool, setIsHighSchool] = useState(false);
  const [isElementary, setIsElementary] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryDescription, setCategoryDescription] = useState('');
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editCategory, setEditCategory] = useState<Category | null>(null);
  const [error, setError] = useState('');

  // Fetch categories from the server
  const fetchCategories = useCallback(async () => {
    try {
      const regionId = authStatus.metadata?.regionId;
      const rawData = await getCategories(regionId);
      const data: Category[] = rawData.map((category: any) => ({
        id: category.id,
        name: category.name,
        description: category.description,
        isHighSchool: category.isHighSchool ?? false,
        isElementary: category.isElementary ?? false,
      }));
      setCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, [authStatus.metadata?.regionId]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleCreateCategoryDialog = () => {
    setCreateDialogOpen(true);
    setError('');
  };

  const handleCreateCategory = async () => {
    if (!categoryName || !categoryDescription) {
      setError('All fields are required.');
      return;
    }
    if (!isHighSchool && !isElementary) {
      setError('Please select at least one school level.');
      return;
    }
    try {
      await createCategory(
        categoryName,
        categoryDescription,
        isHighSchool,
        isElementary
      );
      fetchCategories();
      setCreateDialogOpen(false);
      setCategoryName('');
      setCategoryDescription('');
      setIsHighSchool(false);
      setIsElementary(false);
    } catch (err) {
      console.error('Error creating category:', err);
      setError('Failed to create category.');
    }
  };

  const handleOpenDeleteDialog = (categoryId: string) => {
    setCategoryToDelete(categoryId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;
    try {
      await deleteCategory(categoryToDelete);
      fetchCategories();
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    } catch (err) {
      console.error('Error deleting category:', err);
    }
  };

  const handleOpenEditDialog = (category: Category) => {
    setEditCategory(category);
    setEditDialogOpen(true);
    setError('');
  };

  const handleEditCategory = async () => {
    if (!editCategory) return;
    if (!editCategory.name || !editCategory.description) {
      setError('All fields are required.');
      return;
    }
    if (!editCategory.isHighSchool && !editCategory.isElementary) {
      setError('Please select at least one school level.');
      return;
    }
    try {
      await updateCategory(editCategory.id, editCategory);
      fetchCategories();
      setEditDialogOpen(false);
      setEditCategory(null);
    } catch (err) {
      console.error('Error editing category:', err);
      setError('Failed to update category.');
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Category Management
      </Typography>
      <Button variant="contained" onClick={handleCreateCategoryDialog}>
        Create New Category
      </Button>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Education Levels</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map((category) => (
              <TableRow key={category.id}>
                <TableCell>{category.id}</TableCell>
                <TableCell>{category.name}</TableCell>
                <TableCell>{category.description}</TableCell>
                <TableCell>
                  {category.isHighSchool && category.isElementary
                    ? 'Both'
                    : category.isHighSchool
                      ? 'High School'
                      : category.isElementary
                        ? 'Elementary School'
                        : 'N/A'}
                </TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenEditDialog(category)}
                  >
                    <Pencil size={20} />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleOpenDeleteDialog(category.id)}
                  >
                    <Trash2 size={20} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Category Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      >
        <DialogTitle>Add a New Category</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Category Name"
            fullWidth
            variant="outlined"
            margin="dense"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
          />
          <TextField
            label="Description"
            fullWidth
            variant="outlined"
            margin="dense"
            value={categoryDescription}
            onChange={(e) => setCategoryDescription(e.target.value)}
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Applicable to:</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isHighSchool}
                  onChange={() => setIsHighSchool(!isHighSchool)}
                />
              }
              label="High School"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isElementary}
                  onChange={() => setIsElementary(!isElementary)}
                />
              }
              label="Elementary School"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateCategory}
            variant="contained"
            color="primary"
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Category Name"
            fullWidth
            margin="dense"
            value={editCategory?.name || ''}
            onChange={(e) =>
              setEditCategory((prev) =>
                prev ? { ...prev, name: e.target.value } : null
              )
            }
          />
          <TextField
            label="Description"
            fullWidth
            margin="dense"
            value={editCategory?.description || ''}
            onChange={(e) =>
              setEditCategory((prev) =>
                prev ? { ...prev, description: e.target.value } : null
              )
            }
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Applicable to:</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editCategory?.isHighSchool || false}
                  onChange={() =>
                    setEditCategory((prev) =>
                      prev
                        ? { ...prev, isHighSchool: !prev.isHighSchool }
                        : null
                    )
                  }
                />
              }
              label="High School"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editCategory?.isElementary || false}
                  onChange={() =>
                    setEditCategory((prev) =>
                      prev
                        ? { ...prev, isElementary: !prev.isElementary }
                        : null
                    )
                  }
                />
              }
              label="Elementary School"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditCategory}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this category?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteCategory}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoriesManagement;
